import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import nausLogo from '../assets/naus-logo.jpeg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if member is logged in
  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    setIsMemberLoggedIn(!!token);
  }, []);

  // Check screen size and update state
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close menu when screen size changes to wide
  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpen(false);
    }
  }, [isMobile]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/membership', label: 'Membership' },
    { path: '/training', label: 'Training' },
    { path: '/news', label: 'News' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberEmail');
    setIsMemberLoggedIn(false);
    navigate('/');
  };

  return (
    <header className="mobile-header" style={{
      backgroundColor: 'var(--white)',
      boxShadow: 'var(--shadow)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      width: '100%',
      borderBottom: '2px solid var(--primary-blue)'
    }}>
      <div className="container" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '1rem 0',
        width: '100%'
      }}>
        {/* Logo with colorful border */}
        <Link to="/" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          textDecoration: 'none',
        }}>
          <img 
            src={nausLogo} 
            alt="NAUS Logo" 
            style={{ 
              width: isMobile ? '50px' : '60px', 
              height: isMobile ? '50px' : '60px', 
              borderRadius: '50%',
              objectFit: 'contain'
            }}
          />
        </Link>

        {/* Desktop Navigation - Shows on wide screens */}
        {!isMobile && (
          <nav className="desktop-nav" style={{ 
            display: 'flex', 
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="nav-link"
                style={{
                  color: isActive(link.path) ? 'var(--primary-purple)' : 'var(--dark-gray)',
                  textDecoration: 'none',
                  fontWeight: isActive(link.path) ? 'bold' : 'normal',
                  borderBottom: isActive(link.path) ? '2px solid var(--primary-purple)' : 'none',
                  paddingBottom: '2px',
                  whiteSpace: 'nowrap',
                  fontSize: '0.9rem',
                  padding: '0.5rem 0.75rem'
                }}
              >
                {link.label}
              </Link>
            ))}

            {/* Member Login/Profile Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {isMemberLoggedIn ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Link 
                    to="/member-dashboard" 
                    style={{ 
                      color: 'var(--primary-blue)', 
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      padding: '0.5rem 0.75rem'
                    }}
                  >
                    Member Dashboard
                  </Link>
                  <button 
                    onClick={handleLogout}
                    style={{
                      background: 'var(--primary-red)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link 
                  to="/member-login" 
                  style={{ 
                    color: 'var(--primary-teal)', 
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    padding: '0.5rem 0.75rem'
                  }}
                >
                  Member Login
                </Link>
              )}
            </div>
          </nav>
        )}

        {/* Mobile Menu Button - Only shows on mobile */}
        {isMobile && (
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="mobile-menu-btn"
            style={{ 
              display: 'block', 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              color: 'var(--primary-green)',
              fontSize: '1.5rem',
              flexShrink: 0,
              padding: '0.5rem'
            }}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
        )}
      </div>

      {/* Mobile Dropdown Menu - Only shows on mobile when menu is open */}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '70px' : '80px', // Adjust based on header height
          left: 0,
          right: 0,
          backgroundColor: 'var(--white)',
          boxShadow: 'var(--shadow)',
          zIndex: 99,
          padding: '1rem',
          borderBottom: '2px solid var(--primary-blue)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="nav-link"
                style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  color: isActive(link.path) ? 'var(--primary-purple)' : 'var(--dark-gray)',
                  textDecoration: 'none',
                  fontWeight: isActive(link.path) ? 'bold' : 'normal',
                  borderBottom: '1px solid var(--light-gray)',
                  transition: 'background-color 0.2s ease',
                  fontSize: '1rem',
                  backgroundColor: isActive(link.path) ? 'var(--light-green)' : 'transparent'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile Member Login/Profile */}
            <div style={{ 
              padding: '0.75rem 1rem', 
              borderTop: '1px solid var(--light-gray)',
              backgroundColor: 'var(--light-off-white)'
            }}>
              {isMemberLoggedIn ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Link 
                    to="/member-dashboard" 
                    style={{ 
                      color: 'var(--primary-blue)', 
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      marginBottom: '0.5rem',
                      fontSize: '1rem',
                      padding: '0.5rem 0'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Member Dashboard
                  </Link>
                  <button 
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    style={{
                      background: 'var(--primary-red)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      width: '100%',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link 
                  to="/member-login" 
                  style={{ 
                    color: 'var(--primary-teal)', 
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    padding: '0.5rem 0',
                    display: 'block'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Member Login
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;